// Drafts list / dashboard. Shows existing drafts, lets you create a new one,
// archive or delete from a row menu.

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, FileText, Trash2, MoreHorizontal } from 'lucide-react';
import { useDraftList } from '../hooks/useDraftList';
import { draftsApi } from '../lib/api';
import { makeDefaultDraftBlocks } from './blocks';

export default function DraftsListPage() {
  const { drafts, loading, err, refresh } = useDraftList();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  async function newDraft() {
    setCreating(true);
    try {
      const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const result = await draftsApi.create({
        name: `Untitled · ${today}`,
        blocks: makeDefaultDraftBlocks(),
      });
      navigate(`/draft/${result.id}`);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
      setCreating(false);
    }
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-4xl mx-auto py-10 px-6">
        <header className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl text-slate-900">Drafts</h1>
            <p className="text-sm text-slate-500 mt-1">Newsletter editions, latest first.</p>
          </div>
          <button onClick={newDraft} disabled={creating} className="btn-primary py-2 px-4">
            <Plus size={14} /> {creating ? 'Creating…' : 'New draft'}
          </button>
        </header>

        {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : drafts.length === 0 ? (
          <EmptyState onCreate={newDraft} />
        ) : (
          <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
            {drafts.map((d) => (
              <DraftRow key={d.id} draft={d} onChange={refresh} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="border-2 border-dashed border-slate-200 rounded-lg p-12 text-center">
      <FileText size={28} className="mx-auto text-slate-300 mb-2" />
      <p className="text-sm font-medium text-slate-700">No drafts yet</p>
      <p className="text-xs text-slate-500 mt-1 mb-4">Spin up your first edition with the default block layout.</p>
      <button onClick={onCreate} className="btn-primary py-2 px-4 mx-auto">
        <Plus size={14} /> Create your first draft
      </button>
    </div>
  );
}

interface RowProps {
  draft: import('@shared/types').DraftListItem;
  onChange: () => Promise<void> | void;
}

function DraftRow({ draft, onChange }: RowProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  async function del(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`Delete "${draft.name}"? This can't be undone.`)) return;
    await draftsApi.delete(draft.id);
    await onChange();
  }

  return (
    <li className="relative">
      <Link to={`/draft/${draft.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
        <FileText size={16} className="text-slate-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">{draft.name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {new Date(draft.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {' · '}
            {draft.block_count} blocks
            {draft.subject && <> · <span className="italic text-slate-400 truncate">{draft.subject}</span></>}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold ${
          draft.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
          draft.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
          draft.status === 'archived' ? 'bg-slate-100 text-slate-500' :
          'bg-slate-100 text-slate-600'
        }`}>{draft.status}</span>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen((o) => !o); }}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 opacity-0 group-hover:opacity-100"
        >
          <MoreHorizontal size={14} />
        </button>
      </Link>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-3 top-12 z-30 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-40">
            <button onClick={del} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </>
      )}
    </li>
  );
}
